import { useEffect, useRef, type RefObject } from 'react';
import { useLocation, useNavigationType } from 'react-router';

/**
 * Keeps the reader at the tab bar when switching profile tabs (stakeholder
 * ask: maintain position across tab clicks). The tab links set
 * `preventScrollReset` so the router's top reset stands down, and this hook
 * aligns the nav to the viewport top on every tab change — the reader stays
 * "at the tabs" without ever landing mid-content with the nav scrolled away
 * (the raw keep-the-offset variant was tried and dropped for exactly that).
 *
 * Skips the initial mount, same-tab re-renders (query/filter changes),
 * cross-professional navigations (a fresh landing, not a tab switch), and
 * POP navigations (Back restores the browser's own saved position).
 */
export const useTabScrollAnchor = (
    navRef: RefObject<HTMLElement | null>,
): void => {
    const { pathname } = useLocation();
    const navigationType = useNavigationType();
    const profileBase = pathname.split('/').slice(0, 3).join('/');
    const tabKey = pathname.replace(/\/+$/, '');
    const previousTabRef = useRef({ profileBase, tabKey });

    useEffect(() => {
        const previous = previousTabRef.current;
        previousTabRef.current = { profileBase, tabKey };
        if (previous.tabKey === tabKey) return;
        if (previous.profileBase !== profileBase) return;
        if (navigationType === 'POP') return;
        navRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
        // navRef is a stable ref object; only navigation identity re-runs this.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabKey, profileBase, navigationType]);
};
